import { useEffect, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ResourceList } from "@/components/common/ResourceCard";
import { SecretRow } from "@/components/envSecrets/SecretRow";
import { DeviceSecretNotice } from "@/components/envSecrets/DeviceSecretNotice";
import { SecretDialog } from "@/components/envSecrets/SecretDialog";
import { deleteSecret, listSecrets, type Secret } from "@/lib/api";
import { useToastStore } from "@/stores/useToastStore";

/**
 * 環境のシークレット一覧。設定ダイアログの「シークレット」タブに埋め込まれる。
 * 作成・編集・削除は即時反映され、このエージェントの次回の bash / ターミナル実行から注入される。
 */
export function EnvironmentSecretsPanel({
	environmentId,
	onDevice,
}: {
	environmentId: string;
	onDevice: boolean;
}) {
	const [secrets, setSecrets] = useState<Secret[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const [dialog, setDialog] = useState<{ open: boolean; secret: Secret | null }>({
		open: false,
		secret: null,
	});
	const [pendingDelete, setPendingDelete] = useState<Secret | null>(null);

	useEffect(() => {
		let cancelled = false;
		listSecrets(environmentId)
			.then((data) => {
				if (cancelled) return;
				setSecrets(data);
				setError(null);
			})
			.catch((err: Error) => {
				if (!cancelled) setError(err.message);
			});
		return () => {
			cancelled = true;
		};
	}, [environmentId, refreshKey]);

	const handleDeleteConfirm = async () => {
		if (!pendingDelete) return;
		await deleteSecret(environmentId, pendingDelete.id);
		useToastStore.getState().show({
			type: "success",
			message: `シークレット「${pendingDelete.name}」を削除しました。`,
		});
		setPendingDelete(null);
		setRefreshKey((k) => k + 1);
	};

	return (
		<div
			data-testid="environment-secrets-panel"
			style={{
				flex: 1,
				minHeight: 0,
				overflowY: "auto",
				padding: "20px 24px",
				display: "flex",
				flexDirection: "column",
				gap: 16,
			}}
		>
			<div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
				<span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
					<span
						style={{
							fontSize: "var(--text-caption)",
							lineHeight: "var(--leading-body)",
							color: "var(--fg-2)",
						}}
					>
						このエージェントの bash
						とターミナルに環境変数として注入されます。エージェントに見えるのはプレースホルダだけで、許可ホストへの
						HTTP(S) リクエスト送信時にのみ実値へ置き換わります。
					</span>
					{onDevice && <DeviceSecretNotice />}
				</span>
				<span style={{ display: "flex", minWidth: 0 }}>
					<ConfirmButton
						label="シークレットを作成"
						icon="plus"
						loading={false}
						variant="primary"
						size="sm"
						onClick={() => setDialog({ open: true, secret: null })}
						data-testid="create-secret-button"
					/>
				</span>
			</div>
			<SecretDialog
				open={dialog.open}
				environmentId={environmentId}
				onDevice={onDevice}
				secret={dialog.secret}
				onClose={() => setDialog((d) => ({ ...d, open: false }))}
				onSaved={(saved) => {
					useToastStore.getState().show({
						type: "success",
						message: dialog.secret
							? `シークレット「${saved.name}」を更新しました。`
							: `シークレット「${saved.name}」を作成しました。`,
					});
					setRefreshKey((k) => k + 1);
				}}
			/>
			<ConfirmDialog
				open={pendingDelete !== null}
				title="シークレットを削除"
				message={`「${pendingDelete?.name}」を削除しますか？このエージェントの次回の実行から、この環境変数は注入されなくなります。`}
				confirmLabel="削除"
				cancelLabel="キャンセル"
				onConfirm={handleDeleteConfirm}
				onCancel={() => setPendingDelete(null)}
			/>

			{error && (
				<span style={{ fontSize: "var(--text-caption)", color: "var(--danger)" }}>{error}</span>
			)}
			{!error && secrets === null && (
				<span style={{ fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
					シークレットを読み込んでいます…
				</span>
			)}
			{!error && secrets?.length === 0 && (
				<span
					data-testid="secrets-empty"
					style={{ fontSize: "var(--text-caption)", color: "var(--fg-3)" }}
				>
					シークレットはありません。「シークレットを作成」から作成できます。
				</span>
			)}
			{!error && secrets && secrets.length > 0 && (
				<ResourceList>
					{secrets.map((secret) => (
						<SecretRow
							key={secret.id}
							secret={secret}
							onEdit={() => setDialog({ open: true, secret })}
							onDelete={() => setPendingDelete(secret)}
						/>
					))}
				</ResourceList>
			)}
		</div>
	);
}
